import { Form, Input, Select } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getApi, postApi, putApi } from 'apis';
import Buttons from 'components/Global/Buttons';
import { FC, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';

const AddCostCenterBody: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const {
		closeDrawerByAnimation,
		fetchCostCenterData,
		editSelected,
		branchOptions,
	} = props;
	const [isSelected, setIsSelected] = useState<boolean>();
	const [isSelectedDepartment, setIsSelectedDepartment] = useState<boolean>();
	const [isLoading, setIsLoading] = useState<boolean>();
	const [departmentOptions, setDepartmentOptions] = useState([]);
	const [selectedBranch, setSelectedBranch] = useState(
		editSelected ? editSelected?.branchId : null
	);
	const [selectedDepartment, setSelectedDepartment] = useState(
		editSelected ? editSelected?.departmentId : null
	);

	const [showBranchWarning, setShowBranchWarning] = useState<boolean>(false);
	const [showDepartmentWarning, setShowDepartmentWarning] =
		useState<boolean>(false);

	const buttons = [
		{
			text: `Cancel`,
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: loading,
			onclick: () => {
				closeDrawerByAnimation();
			},
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: `Save`,
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: () => {},
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
	];

	const handleEditSubmit = async (values: any) => {
		let data: any = {
			costCenterId: editSelected?.id,
		};

		if (selectedBranch && selectedDepartment) {
			data = {
				...data,
				branchId: selectedBranch,
				departmentId: selectedDepartment,
				name: values.name,
				code: values.code,
			};
			await updateCostCentertData(data);
		} else {
			setIsSelected(!!selectedBranch);
			setIsSelectedDepartment(!!selectedDepartment);
		}
	};

	const handleAddSubmit = async (values: any) => {
		if (selectedBranch && selectedDepartment) {
			setIsSelected(true);
			const finalData = {
				branchId: selectedBranch,
				departmentId: selectedDepartment,
				name: values.name,
				code: values.code,
			};
			await addDepartment(finalData);
		} else {
			setIsSelected(!!selectedBranch);
			setIsSelectedDepartment(!!selectedDepartment);
		}
	};

	const handleSubmit = async (values: any) => {
		if (editSelected) {
			await handleEditSubmit(values);
		} else {
			await handleAddSubmit(values);
		}
	};

	// If form fails
	const onFinishFailed = () => {
		selectedBranch ? setIsSelected(true) : setIsSelected(false);
		selectedDepartment
			? setIsSelectedDepartment(true)
			: setIsSelectedDepartment(false);
	};

	const fetchDepartmentCodes = async (branchId: string) => {
		setIsLoading(true);
		try {
			const apiRes: any = await getApi(`/department/${branchId}`);

			if (apiRes.data.data) {
				const departments = apiRes.data.data.map((department: any) => {
					return {
						id: department.id,
						name: department.name,
					};
				});
				setDepartmentOptions(departments);
			}
		} catch (err) {
			toastText('Something went wrong in fetching department', 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const handleBranchChange = (branchId: any) => {
		setSelectedBranch(branchId);
		setIsSelected(true);

		// Show inline warning if branch is being changed during edit
		if (editSelected && editSelected.branchId !== branchId) {
			setShowBranchWarning(true);
		} else {
			setShowBranchWarning(false);
		}

		fetchDepartmentCodes(branchId);
		setSelectedDepartment(null);
		setShowDepartmentWarning(false); // Reset department warning when branch changes
	};

	const handleDepartmentChange = (departmentId: any) => {
		setSelectedDepartment(departmentId);
		setIsSelectedDepartment(true);

		// Show inline warning if department is being changed during edit
		if (editSelected && editSelected.departmentId !== departmentId) {
			setShowDepartmentWarning(true);
		} else {
			setShowDepartmentWarning(false);
		}
	};

	useEffect(() => {
		selectedBranch && fetchDepartmentCodes(selectedBranch);
	}, []);

	const updateCostCentertData = async (departmentData: any) => {
		try {
			setLoading(true);
			const response = await putApi('/costCenter', departmentData);
			fetchCostCenterData();
			closeDrawerByAnimation();
			let message =
				response?.data?.message || 'Cost center updated successfully';
			toastText(message, 'success');
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in updating cost center';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	};

	const addDepartment = async (finalData: any) => {
		try {
			setLoading(true);
			const response = await postApi('/costCenter', finalData);
			fetchCostCenterData();
			closeDrawerByAnimation();
			let message =
				response?.data?.message || 'Cost center created successfully';
			toastText(message, 'success');
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong when creating to cost center';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	};
	const isEditMode = !!editSelected;

	return (
		<div className={styles['side-drawer-body']}>
			<Form
				name="basic"
				className={styles['side-drawer-form']}
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				onFinish={handleSubmit}
				onFinishFailed={onFinishFailed}
				initialValues={editSelected}
				autoComplete="off"
			>
				<p className={styles['form-container-head-warning']}>
					<b>
						{' '}
						<sup>*</sup>
					</b>{' '}
					Indicated mandatory fields
				</p>
				<div className={styles['side-drawer-form__inputs']}>
					<label className={styles['side-drawer-form__role--label']}>
						{'Cost Code'} <span className="required-color">*</span>
					</label>
					<Form.Item
						name={'code'}
						rules={[
							{
								required: true,
								message: 'Please enter the Cost Center Code',
								validateTrigger: 'onChange',
								validator: (_, value) =>
									value && value.trim() !== ''
										? Promise.resolve()
										: Promise.reject(
												new Error('Please enter value')
										  ),
							},
						]}
						wrapperCol={{ span: 24 }}
					>
						<Input
							className="remove-antd-input-effect"
							maxLength={150}
							size="large"
							disabled={false}
							type={'text'}
						/>
					</Form.Item>
				</div>
				<div className={styles['side-drawer-form__inputs']}>
					<label className={styles['side-drawer-form__role--label']}>
						{'Cost Name'} <span className="required-color">*</span>
					</label>
					<Form.Item
						name={'name'}
						rules={[
							{
								required: true,
								message: 'Please enter the Cost Center Name',
								validateTrigger: 'onChange',
								validator: (_, value) =>
									value && value.trim() !== ''
										? Promise.resolve()
										: Promise.reject(
												new Error('Please enter value')
										  ),
							},
						]}
						wrapperCol={{ span: 24 }}
					>
						<Input
							className="remove-antd-input-effect"
							maxLength={150}
							size="large"
							disabled={false}
							type={'text'}
						/>
					</Form.Item>
					<Form.Item
						className="side-drawer-form__role"
						name={'branchId'}
					>
						<label
							className={styles['side-drawer-form__role--label']}
						>
							{'Select Branch'}{' '}
							<span className="required-color">*</span>
						</label>

						<Select
							placeholder="Select Branch"
							className={styles['side-drawer-form--select']}
							size="large"
							disabled={isEditMode}
							value={selectedBranch}
							onSelect={handleBranchChange}
						>
							{branchOptions?.map((branch: any, key: number) => {
								return (
									<Select.Option
										value={branch?.id}
										key={branch?.id}
									>
										{branch?.name}
									</Select.Option>
								);
							})}
						</Select>
						{isSelected === false && (
							<p className="ant-form-item-explain-error">
								Please select a Branch
							</p>
						)}
					</Form.Item>
					<Form.Item
						className="side-drawer-form__role"
						name={'departmentId'}
					>
						<label
							className={styles['side-drawer-form__role--label']}
						>
							{'Select Department'}{' '}
							<span className="required-color">*</span>
						</label>

						<Select
							placeholder="Select Department"
							className={styles['side-drawer-form--select']}
							size="large"
							loading={isLoading}
							disabled={
								isEditMode ||
								!(isSelected || selectedBranch) ||
								isLoading
							}
							value={selectedDepartment}
							onSelect={handleDepartmentChange}
						>
							{departmentOptions?.map(
								(department: any, key: number) => {
									return (
										<Select.Option
											value={department?.id}
											key={department?.id}
										>
											{department?.name}
										</Select.Option>
									);
								}
							)}
						</Select>
						{isSelectedDepartment === false && (
							<p className="ant-form-item-explain-error">
								Please select a Department
							</p>
						)}
					</Form.Item>
					{/* Department change warning */}
					{(showDepartmentWarning || showBranchWarning) && (
						<div className={styles['branch-warning']}>
							<ExclamationCircleOutlined
								className={styles['warning-icon']}
							/>
							<span>
								<strong>Warning:</strong> Changing the
								department or branch will remove cost center
								from all employees related to this cost center
							</span>
						</div>
					)}
				</div>
				<div className={styles['side-drawer-form__buttons']}>
					<Form.Item>
						<Buttons buttons={buttons} />
					</Form.Item>
				</div>
			</Form>
		</div>
	);
};

export default AddCostCenterBody;
